<template>
  <v-dialog v-model="isOpen" persistent max-width="500px">
    <v-card>
      <v-card-title>
        <span class="headline">Request Weblogin</span>
        <v-spacer></v-spacer>
        <v-btn icon @click="closeDialog" :disabled="isLoading">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <div v-if="!showSuccessMessage">
          <p>
            <strong>Important:</strong> To request a login link, you must have <strong>already registered your email address</strong> with TinyGS.
          </p>
          <p>
            This is typically done by using the <code>/weblogin</code> command with our
            <a href="https://t.me/tinygs_personal_bot" target="_blank" rel="noopener noreferrer" title="Open TinyGS Personal Bot on Telegram">Telegram bot (@tinygs_personal_bot)</a>.
          </p>
          <p class="mt-3">
            New to TinyGS or haven't registered your email yet?
            <a href="#" @click.prevent="switchToRegisterDialog">View registration instructions</a>.
          </p>
        </div>
        <v-text-field
          v-model="email"
          label="Enter your registered email"
          type="email"
          @input="validateEmail"
          :error-messages="emailError ? [emailError] : []"
          :disabled="showSuccessMessage"
          :class="{ 'input-error': emailError, 'mt-4': !showSuccessMessage }"
        ></v-text-field>

        <div id="login-turnstile-container" v-show="!showSuccessMessage"></div>
        <div v-if="turnstileError && !showSuccessMessage" class="error-message">{{ turnstileError }}</div>
        <div v-if="requestMessage" :class="showSuccessMessage ? 'request-message' : (isError ? 'error-message' : 'request-message')">{{ requestMessage }}</div>
      </v-card-text>
      <div v-if="timerActive" class="mt-2 blue--text text--darken-2" style="text-align:center;">
        Please wait {{ timerCountdown }} second<span v-if="timerCountdown !== 1">s</span> before requesting again.
      </div>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" @click="requestLogin" :loading="isLoading" :disabled="!isFormValid || isLoading || showSuccessMessage || timerActive">
          <template v-if="timerActive">
            Request Login (wait {{ timerCountdown }}s)
          </template>
          <template v-else>
            Request Login
          </template>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent, ref, computed, watch, onBeforeUnmount, nextTick } from 'vue';
import axios from '../../plugins/axios';
import { useGlobalTimer } from '@/composables/useGlobalTimer';

export default defineComponent({
  name: "LoginDialog",
  props: {
    value: {
      type: Boolean,
      required: true
    }
  },
  emits: ['input', 'open-register'],
  setup(props, { emit }) {
    // State management using refs
    const email = ref('');
    const emailError = ref('');
    const turnstileError = ref('');
    const turnstileToken = ref(null);
    const turnstileInstance = ref(null);
    const isFormValid = ref(false);
    const isLoading = ref(false);
    const requestMessage = ref('');
    const showSuccessMessage = ref(false);
    const isError = ref(false);

    // Computed property for v-model functionality
    const isOpen = computed({
      get: () => props.value,
      set: (value) => emit('input', value)
    });

    // Email validation function
    const validateEmail = () => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!email.value) {
        emailError.value = 'Email is required';
      } else if (!emailRegex.test(email.value)) {
        emailError.value = 'Invalid email format';
      } else {
        emailError.value = '';
      }
      validateForm();
    };

    // Form validation
    const validateForm = () => {
      isFormValid.value = emailError.value === '' && turnstileToken.value !== null && email.value !== '';
    };

    // Dialog close handler
    const closeDialog = () => {
      if (!showSuccessMessage.value) {
        email.value = '';
        emailError.value = '';
        turnstileError.value = '';
        requestMessage.value = '';
        turnstileToken.value = null;
      }
      if (turnstileInstance.value) {
        try {
          window.turnstile.remove('#login-turnstile-container');
        } catch(e) {
          console.warn("Error removing login turnstile:", e);
        }
        turnstileInstance.value = null;
      }
      showSuccessMessage.value = false;
      isError.value = false;
      emit('input', false);
    };

    // Login request handler
    const requestLogin = async () => {
      validateEmail();
      if (!isFormValid.value) {
        return;
      }
      isLoading.value = true;
      requestMessage.value = '';
      isError.value = false;

      try {
        await axios.post('/weblogin/', {
          email: email.value,
          turnstile: turnstileToken.value
        });
        requestMessage.value = "Request successful! Please check your email.";
        showSuccessMessage.value = true;
        isError.value = false;
        startTimer(30); // 30 segundos para este diálogo
        setTimeout(() => {
          closeDialog();
        }, 2000);
      } catch (error) {
        console.error('Error sending login request:', error);
        requestMessage.value = error.response?.data?.message || "An error occurred, please try again later.";
        isError.value = true;
        if (turnstileInstance.value) {
          window.turnstile.reset(turnstileInstance.value);
          turnstileToken.value = null;
          validateForm();
        }
      } finally {
        isLoading.value = false;
      }
    };

    // Turnstile initialization
    const initializeTurnstile = () => {
      if (window.turnstile) {
        const container = document.getElementById('login-turnstile-container');
        if (container && !turnstileInstance.value) {
          turnstileInstance.value = window.turnstile.render('#login-turnstile-container', {
            sitekey: '0x4AAAAAAA4Ls89Ok1pJ_hw-',
            callback: (token) => {
              turnstileToken.value = token;
              turnstileError.value = '';
              validateForm();
            },
            'error-callback': () => {
              turnstileError.value = "Captcha Error, please try again";
              turnstileToken.value = null;
              validateForm();
            },
            'expired-callback': () => {
              turnstileError.value = "Captcha Expired, please try again";
              turnstileToken.value = null;
              validateForm();
            },
          });
        }
      } else {
        console.error("Turnstile script not loaded.");
        turnstileError.value = "Captcha service is unavailable.";
      }
    };

    // Watch for dialog open
    watch(() => props.value, (newVal) => {
      if (newVal) {
        nextTick(() => {
          if (turnstileInstance.value) {
            try {
              window.turnstile.remove('#login-turnstile-container');
            } catch(e) {
              console.warn("Failed to remove login turnstile:", e);
            }
            turnstileInstance.value = null;
          }
          initializeTurnstile();
        });
      }
    });

    // Cleanup on component unmount
    onBeforeUnmount(() => {
      if (turnstileInstance.value) {
        try {
          window.turnstile.remove('#login-turnstile-container');
        } catch(e) {
          console.warn("Error removing login turnstile on destroy:", e);
        }
        turnstileInstance.value = null;
      }
    });

    const switchToRegisterDialog = () => {
      emit('input', false); // Close current dialog
      emit('open-register'); // Request parent to open register dialog
    };

    const { timerActive, timerCountdown, startTimer } = useGlobalTimer();

    return {
      email,
      emailError,
      turnstileError,
      isFormValid,
      isLoading,
      requestMessage,
      showSuccessMessage,
      isError,
      isOpen,
      validateEmail,
      closeDialog,
      requestLogin,
      timerActive,
      timerCountdown,
      switchToRegisterDialog
    };
  }
});
</script>

<style scoped>
.input-error .v-input__control {
  border-color: red !important;
}
.error-message {
  color: red;
  font-size: 0.85rem;
  margin-top: 5px;
}
.request-message {
  color: green;
  font-size: 0.9rem;
  margin-top: 10px;
}
</style>